/**
 * MK-2 — Select that works: what part of a mark the pointer landed on, and how
 * a drag of that part changes the mark. Pure, in CONTENT coordinates (the
 * editor converts with toContent before and toSheet after). No DOM.
 *
 * Parts:
 *   point            'move'
 *   arrow            'start' (the tail), 'end' (the head), 'move'
 *   bracket          'start' (A), 'end' (B), 'move'   — MK-6, same two points as an arrow
 *   cloud/highlight  'nw' 'ne' 'sw' 'se' (corners), 'move'
 *   pen/freehand     'move'
 */

const near = (ax, ay, bx, by, t) => Math.hypot(ax - bx, ay - by) <= t;

export const rectOf = (m) => {
  const w = m.width || 0;
  const h = m.height || 0;
  return {
    x1: Math.min(m.x, m.x + w), y1: Math.min(m.y, m.y + h),
    x2: Math.max(m.x, m.x + w), y2: Math.max(m.y, m.y + h),
  };
};

/** The handle squares a selected mark shows, in content coordinates. */
export const handlesOf = (m) => {
  if (m.type === 'point') return [{ part: 'move', x: m.x, y: m.y }];
  if (m.type === 'arrow' || m.type === 'bracket') {
    return [
      { part: 'start', x: m.x, y: m.y },
      { part: 'end', x: m.x + (m.width || 100), y: m.y + (m.height || 0) },
    ];
  }
  if (m.type === 'pen' || m.type === 'freehand') return [];
  const r = rectOf(m);
  return [
    { part: 'nw', x: r.x1, y: r.y1 }, { part: 'ne', x: r.x2, y: r.y1 },
    { part: 'sw', x: r.x1, y: r.y2 }, { part: 'se', x: r.x2, y: r.y2 },
  ];
};

/**
 * Which part of this mark is under `c`, or null. `grab` is the handle reach and
 * `thin` the line reach, both already in content units (screen px / zoom).
 */
export const hitPart = (m, c, grab = 8, thin = 6) => {
  for (const h of handlesOf(m)) {
    if (h.part !== 'move' && near(c.x, c.y, h.x, h.y, grab)) return h.part;
  }
  if (m.type === 'point') return near(c.x, c.y, m.x, m.y, grab + 4) ? 'move' : null;
  if (m.type === 'arrow' && m.head === 'start' && m.box) {
    // MK-3 — the note box at the far end: inside it drags the box (part 'end')
    const bx = m.x + (m.width || 100);
    const by = m.y + (m.height || 0);
    if (Math.abs(c.x - bx) <= m.box.w / 2 && Math.abs(c.y - by) <= m.box.h / 2) return 'end';
  }
  if (m.type === 'arrow' || m.type === 'bracket') {
    const x2 = m.x + (m.width || 100);
    const y2 = m.y + (m.height || 0);
    const len = Math.hypot(x2 - m.x, y2 - m.y) || 1;
    const d = Math.abs((x2 - m.x) * (m.y - c.y) - (m.x - c.x) * (y2 - m.y)) / len;
    const along = ((c.x - m.x) * (x2 - m.x) + (c.y - m.y) * (y2 - m.y)) / (len * len);
    return d <= thin && along >= 0 && along <= 1 ? 'move' : null;
  }
  if (m.type === 'pen' || m.type === 'freehand') {
    const pts = [{ x: m.x, y: m.y }, ...(m.points || [])];
    return pts.some((p) => near(c.x, c.y, p.x, p.y, thin + 2)) ? 'move' : null;
  }
  const r = rectOf(m);
  return c.x >= r.x1 - 4 && c.x <= r.x2 + 4 && c.y >= r.y1 - 4 && c.y <= r.y2 + 4 ? 'move' : null;
};

/** The mark after dragging `part` by (dx, dy). Returns a new object. */
export const applyDrag = (m, part, dx, dy) => {
  if (part === 'move') {
    const out = { ...m, x: m.x + dx, y: m.y + dy };
    if (Array.isArray(m.points)) out.points = m.points.map((p) => ({ x: p.x + dx, y: p.y + dy }));
    return out;
  }
  if (m.type === 'arrow' || m.type === 'bracket') {
    const w = m.width || 100;
    const h = m.height || 0;
    if (part === 'end') return { ...m, width: w + dx, height: h + dy };
    if (part === 'start') return { ...m, x: m.x + dx, y: m.y + dy, width: w - dx, height: h - dy };
    return m;
  }
  // corner of a box: move that corner, keep the opposite one
  const r = rectOf(m);
  let { x1, y1, x2, y2 } = r;
  if (part.includes('w')) x1 += dx;
  if (part.includes('e')) x2 += dx;
  if (part.includes('n')) y1 += dy;
  if (part.includes('s')) y2 += dy;
  return { ...m, x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
};

/**
 * MK-3 — where an arrow's line meets its note box: the point on the box edge
 * aimed at (px, py). Box is centred on (cx, cy) with size w × h.
 */
export const boxEdge = (cx, cy, w, h, px, py) => {
  const dx = px - cx;
  const dy = py - cy;
  if (!dx && !dy) return { x: cx, y: cy };
  const s = Math.min((w / 2) / Math.abs(dx || 1e-9), (h / 2) / Math.abs(dy || 1e-9));
  return { x: cx + dx * s, y: cy + dy * s };
};

/** The cursor to show over a part. */
export const cursorFor = (part) => {
  if (!part) return 'default';
  if (part === 'move' || part === 'start' || part === 'end') return 'move';
  return `${part}-resize`;
};
